const STORAGE_KEY = 'chefman-perfect-pour-hi';
export function createPourGame() {
  return { state: 'idle', score: 0, lives: 3, streak: 0, round: 0, fill: 0, center: .55, band: .22, speed: .28, feedback: 0, result: null };
}
export function nextPour(game, random = Math.random) {
  game.round++;
  game.band = Math.max(.09, .22 - (game.round - 1) * .009);
  // Every shot is a little faster, including long runs beyond the old cap.
  // Taper the increase toward one glass per second to keep late shots playable.
  const completedShots = game.round - 1;
  game.speed = .28 + .72 * completedShots / (completedShots + 30);
  game.center = .25 + game.band / 2 + random() * (.85 - .25 - game.band);
  game.fill = 0; game.result = null; game.feedback = 0; game.state = 'playing';
}
export function stopPour(game) {
  if (game.state !== 'playing') return null;
  const distance = Math.abs(game.fill - game.center);
  const ok = distance <= game.band / 2 + 1e-10;
  const accuracy = ok ? Math.max(0, 1 - distance / (game.band / 2)) : 0;
  const points = ok ? Math.round(20 + accuracy * 80) : 0;
  if (ok) { game.score += points; game.streak++; } else { game.lives--; game.streak = 0; }
  game.result = { ok, accuracy, points, label: ok ? accuracy >= .85 ? 'Perfect pour!' : 'Beautifully brewed.' : game.fill >= 1 ? 'A little too much.' : game.fill < game.center ? 'A little too soon.' : 'Just past the sweet spot.' };
  game.state = 'resolving'; game.feedback = 1.35;
  return game.result;
}
export function advancePour(game, elapsed, random = Math.random) {
  const dt = Math.min(.05, Math.max(0, elapsed));
  if (game.state === 'playing') {
    game.fill = Math.min(1, game.fill + game.speed * dt);
    if (game.fill >= 1) stopPour(game);
  } else if (game.state === 'resolving') {
    game.feedback -= dt;
    if (game.feedback <= 0) {
      if (game.lives <= 0) game.state = 'over'; else nextPour(game, random);
    }
  }
}
export function pausePour(game) {
  if (!['playing', 'resolving'].includes(game.state)) return;
  game.resumeState = game.state; game.state = 'paused';
}
export function resumePour(game) {
  if (game.state === 'paused') { game.state = game.resumeState; delete game.resumeState; }
}

if (typeof window !== 'undefined' && !customElements.get('perfect-pour')) {
  customElements.define('perfect-pour', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.controller.signal });
      this.game = createPourGame(); this.canvas = this.querySelector('canvas'); this.ctx = this.canvas.getContext('2d');
      this.best = 0;
      try { this.best = Math.max(0, parseInt(localStorage.getItem(STORAGE_KEY), 10) || 0); } catch {}
      this.logo = new Image(); listen(this.logo, 'load', () => this.draw()); this.logo.src = this.dataset.logoSrc;
      this.stopButton = this.querySelector('[data-stop]');
      listen(this.querySelector('[data-play]'), 'click', () => {
        if (this.game.state === 'paused') resumePour(this.game);
        else { this.game = createPourGame(); nextPour(this.game); }
        this.querySelector('[data-overlay]').hidden = true; this.sync(); this.stopButton.focus({ preventScroll: true }); this.run();
      });
      listen(this.stopButton, 'click', () => this.stop());
      listen(this.canvas, 'pointerdown', event => {
        if (event.button !== 0) return;
        // A non-focusable canvas otherwise moves focus to the page after this
        // handler and auto-pauses play. Cancel that default for mouse and touch.
        event.preventDefault();
        this.stop();
        if (!this.stopButton.disabled) this.stopButton.focus({ preventScroll: true });
      });
      listen(this.querySelector('[data-pause]'), 'click', () => this.pause());
      // A touch can move browser focus without the player leaving the game.
      // Only intentional keyboard navigation should trigger focus-exit pausing.
      listen(document, 'pointerdown', event => {
        this.keyboardNavigation = false;
        this.touchInteraction = event.pointerType === 'touch' || event.pointerType === 'pen';
      }, { capture: true });
      listen(this, 'keydown', event => {
        this.keyboardNavigation = event.key === 'Tab';
        this.touchInteraction = false;
        if (event.target.matches('input,textarea,select') || event.target.isContentEditable || event.repeat) return;
        const key = event.key.toLowerCase();
        if (key === 'p' || key === 'escape') { event.preventDefault(); this.pause(); }
        if ((key === ' ' || key === 'enter') && this.game.state === 'playing' && !event.target.matches('[data-pause]')) { event.preventDefault(); this.stop(); }
      });
      listen(this, 'focusout', event => {
        const keyboardExit = this.keyboardNavigation;
        this.keyboardNavigation = false;
        if (keyboardExit && event.relatedTarget && !this.contains(event.relatedTarget)) this.pause();
      });
      listen(window, 'blur', () => {
        // Mobile Safari focus/blur notifications are not a reliable app-switch signal.
        // Visibility changes below handle real backgrounding on touch devices.
        if (!this.touchInteraction && window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.pause();
      });
      listen(document, 'visibilitychange', () => { if (document.hidden) this.pause(); });
      this.resizeObserver = new ResizeObserver(() => this.resize()); this.resizeObserver.observe(this.canvas);
      this.sync(); this.resize();
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); cancelAnimationFrame(this.frame); this.controller = null; }
    resize() {
      const rect = this.canvas.getBoundingClientRect(); if (!rect.width || !rect.height) return;
      const dpr = Math.min(devicePixelRatio || 1, 2); this.canvas.width = Math.round(rect.width * dpr); this.canvas.height = Math.round(rect.height * dpr); this.draw();
    }
    run() { cancelAnimationFrame(this.frame); this.lastTime = performance.now(); this.frame = requestAnimationFrame(t => this.tick(t)); }
    tick(time) {
      const previous = this.game.state, round = this.game.round;
      advancePour(this.game, (time - this.lastTime) / 1000); this.lastTime = time;
      if (previous !== this.game.state || round !== this.game.round) this.sync();
      this.draw();
      if (['playing', 'resolving'].includes(this.game.state)) this.frame = requestAnimationFrame(t => this.tick(t));
    }
    stop() { if (stopPour(this.game)) { this.sync(); this.draw(); } }
    pause() {
      if (!['playing', 'resolving'].includes(this.game.state)) return;
      pausePour(this.game); cancelAnimationFrame(this.frame); this.sync();
      this.overlay('TAKE YOUR TIME', 'Good coffee can wait.', 'Your shot and score are saved right here.', 'Keep pouring');
      this.querySelector('[data-play]').focus({ preventScroll: true });
    }
    overlay(kicker, title, message, button) {
      this.querySelector('[data-kicker]').textContent = kicker; this.querySelector('[data-title]').textContent = title;
      this.querySelector('[data-message]').textContent = message; this.querySelector('[data-play]').replaceChildren(document.createTextNode(button + ' →'));
      this.querySelector('[data-overlay]').hidden = false;
    }
    sync() {
      const g = this.game;
      if (g.score > this.best) { this.best = g.score; try { localStorage.setItem(STORAGE_KEY, String(this.best)); } catch {} }
      this.querySelector('[data-score]').textContent = g.score; this.querySelector('[data-best]').textContent = this.best;
      const lives = this.querySelector('[data-lives]'); lives.textContent = '● '.repeat(g.lives) + '○ '.repeat(3 - g.lives); lives.setAttribute('aria-label', `${g.lives} lives remaining`);
      this.querySelector('[data-pause]').disabled = !['playing', 'resolving'].includes(g.state);
      // Keep the focused stop control in the tab order between shots; ignore presses during feedback.
      this.stopButton.disabled = ['idle', 'paused', 'over'].includes(g.state);
      this.stopButton.setAttribute('aria-disabled', g.state !== 'playing' ? 'true' : 'false');
      this.stopButton.firstChild.textContent = g.state === 'resolving' ? 'Next shot… ' : 'Stop the pour ';
      const feedback = this.querySelector('[data-feedback]');
      feedback.dataset.result = g.result ? g.result.ok ? 'good' : 'miss' : '';
      this.dataset.result = feedback.dataset.result;
      this.querySelector('[data-miss]').hidden = !(g.state === 'resolving' && g.result && !g.result.ok);
      if (g.state === 'playing') feedback.textContent = `Shot ${String(g.round).padStart(2, '0')} · Aim for the center of the golden zone.`;
      else if (g.result) feedback.textContent = g.result.ok
        ? `${g.result.label} +${g.result.points}${g.streak > 1 ? ' · ' + g.streak + ' in a row' : ''}`
        : `Missed pour · −1 life. ${g.result.label} ${g.lives} ${g.lives === 1 ? 'life' : 'lives'} left.`;
      if (g.state === 'over') {
        this.overlay('ONE MORE COFFEE?', 'That was a good break.', `You scored ${g.score} points across ${g.round} shots. Your personal best is ${this.best}.`, 'Pour again');
        this.querySelector('[data-play]').focus({ preventScroll: true });
      }
    }
    draw() {
      if (!this.ctx) return;
      const c = this.ctx, g = this.game;
      c.setTransform(this.canvas.width / 600, 0, 0, this.canvas.height / 530, 0, 0);
      const rect = (x,y,w,h,r,fill) => { c.fillStyle=fill; c.beginPath(); c.roundRect(x,y,w,h,r); c.fill(); };
      const ellipse = (x,y,rx,ry,fill) => { c.fillStyle=fill;c.beginPath();c.ellipse(x,y,rx,ry,0,0,Math.PI*2);c.fill(); };
      const gradient = (x,y,x2,y2,stops) => { const gr=c.createLinearGradient(x,y,x2,y2);stops.forEach(([n,col])=>gr.addColorStop(n,col));return gr; };
      const text = (str,x,y,size,color,align='left') => { c.font=`${size}px "Instrument Sans", sans-serif`;c.fillStyle=color;c.textAlign=align;c.fillText(str,x,y); };
      c.fillStyle=gradient(0,0,600,530,[[0,'#eff1ef'],[1,'#c8d1d4']]);c.fillRect(0,0,600,530);
      c.strokeStyle='#b9c5ca55';c.lineWidth=1;
      for(let x=0;x<600;x+=100){c.beginPath();c.moveTo(x,0);c.lineTo(x,465);c.stroke();}
      c.beginPath();c.moveTo(0,245);c.lineTo(600,245);c.stroke();
      rect(0,470,600,60,0,gradient(0,470,0,530,[[0,'#c6ced1'],[1,'#aebbc2']]));
      ellipse(304,486,220,19,'#131e291c');
      // A softly beveled midnight machine, with a recessed brew bay.
      rect(109,35,388,443,22,gradient(100,0,500,0,[[0,'#53636f'],[.05,'#263542'],[.8,'#131e29'],[1,'#344650']]));
      rect(120,44,365,136,15,gradient(120,40,470,170,[[0,'#354651'],[1,'#192833']]));
      if (this.logo.complete && this.logo.naturalWidth) c.drawImage(this.logo, 232, 66, 137, 137*this.logo.naturalHeight/this.logo.naturalWidth);
      c.strokeStyle='#83949e55';c.beginPath();c.moveTo(138,99);c.lineTo(467,99);c.stroke();
      // Pressure dial and indicator details are decorative, with no real-world extraction claims.
      ellipse(176,135,24,24,'#7b8a94');ellipse(176,135,21,21,'#17242e');
      c.strokeStyle='#d9e1e2';c.lineWidth=1.5;c.beginPath();c.arc(176,135,15,Math.PI*.8,Math.PI*2.2);c.stroke();
      c.beginPath();c.moveTo(176,135);c.lineTo(185,125);c.stroke();ellipse(176,135,2,2,'#d9e1e2');
      rect(254,112,96,39,6,'#0e1821');text('ESPRESSO',302,128,9,'#98aab4','center');text(String(g.round||1).padStart(2,'0'),302,144,13,'#e5ebe9','center');
      ellipse(423,132,16,16,'#71828d');ellipse(423,132,13,13,'#263641');ellipse(423,132,4,4,['playing','resolving'].includes(g.state)?'#dec394':'#83949e');
      rect(134,181,339,271,[8,8,14,14],gradient(140,181,460,430,[[0,'#0c151d'],[.45,'#253642'],[1,'#111d26']]));
      // Brushed metal group head and portafilter.
      rect(257,174,90,33,[0,0,8,8],gradient(257,0,347,0,[[0,'#83959e'],[.3,'#d3dce0'],[.6,'#9caeb8'],[1,'#627781']]));
      rect(269,203,66,14,[0,0,6,6],'#becbd1');rect(336,189,81,14,6,'#101b24');rect(328,192,20,8,2,'#8b9fa9');
      rect(284,215,9,14,2,'#b0c1c9');rect(312,215,9,14,2,'#b0c1c9');
      rect(130,442,346,31,7,gradient(0,442,0,473,[[0,'#bdcbd0'],[.25,'#7a8d97'],[1,'#475b67']]));
      for(let x=145;x<467;x+=13)rect(x,448,5,14,2,'#334953');
      ellipse(301,440,92,9,'#07101855');ellipse(300,437,82,8,'#b9c8ce');ellipse(300,435,63,5,'#738994');
      // Clear glass: the liquid and stream are clipped inside its tapered body.
      const glass=()=>{c.beginPath();c.moveTo(235,266);c.lineTo(248,420);c.quadraticCurveTo(300,438,352,420);c.lineTo(365,266);c.closePath();};
      c.strokeStyle='#d8e3e9b0';c.lineWidth=7;c.beginPath();c.ellipse(370,338,24,41,.08,-Math.PI*.6,Math.PI*.6);c.stroke();
      c.save();glass();c.clip();
      c.fillStyle=gradient(235,0,365,0,[[0,'#e6f0f025'],[.5,'#f1f5f23b'],[1,'#d7e6ec18']]);c.fillRect(233,259,134,177);
      const bottom=424,height=150,surface=bottom-g.fill*height;
      const top=bottom-(g.center+g.band/2)*height,low=bottom-(g.center-g.band/2)*height;
      c.fillStyle='#ddbe7940';c.fillRect(231,top,140,low-top);
      if(g.fill>0){c.fillStyle=gradient(0,surface,0,428,[[0,'#8a5732'],[.25,'#50301e'],[1,'#281b15']]);c.fillRect(234,surface,134,174);ellipse(300,surface,67,5,'#d5aa68');ellipse(300,surface-1,64,2,'#edce95');}
      if(g.state==='playing'){c.fillStyle='#bd8852';c.fillRect(286,229,5,Math.max(0,surface-228));c.fillStyle='#8f5b33';c.fillRect(314,229,4,Math.max(0,surface-228));}
      c.strokeStyle='#f0d496';c.lineWidth=1.4;c.setLineDash([5,4]);for(const y of [top,low]){c.beginPath();c.moveTo(235,y);c.lineTo(365,y);c.stroke();}c.setLineDash([]);
      c.restore();
      if(g.state==='playing'){c.fillStyle='#bd8852';c.fillRect(286,229,5,37);c.fillStyle='#8f5b33';c.fillRect(314,229,4,37);}
      const missed = g.result && !g.result.ok;
      c.strokeStyle=missed?'#ffac94':'#d9e5e9b3';c.lineWidth=missed?4:2;glass();c.stroke();ellipse(300,266,65,6,'#e2edf020');
      c.beginPath();c.ellipse(300,266,65,6,0,0,Math.PI*2);c.stroke();
      c.strokeStyle='#f4f8f57a';c.lineWidth=3;c.beginPath();c.moveTo(244,282);c.lineTo(254,410);c.stroke();
      text('TARGET',391,(top+low)/2+3,9,'#ecd3a2');c.strokeStyle='#ecd3a2';c.lineWidth=1;c.beginPath();c.moveTo(369,(top+low)/2);c.lineTo(385,(top+low)/2);c.stroke();
      text('THE ART OF A LITTLE PAUSE',300,512,9,'#536774','center');
    }
  });
}
